/**
 * BookerDashboard — landing page for `approved_booker` role (Phase F α).
 *
 * Combines:
 *   1. Quick actions (Create booking, Manage drivers)
 *   2. Upcoming bookings list (mine), with move/cancel actions
 *   3. My drivers (typeahead + + Add new)
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Users, Calendar, Truck } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/lib/auth";
import { statusBadgeClass } from "@/lib/constants";
import { toast } from "sonner";
import { searchCompanyDrivers } from "@/lib/api";

export default function BookerDashboard() {
  const { user, assignments = [] } = useAuth();
  const companyId = assignments.find((a) => a.role === "approved_booker" && a.approved_booker_id)?.approved_booker_id || null;
  const [bookings, setBookings] = useState([]);
  const [drivers,  setDrivers]  = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      setLoading(true);
      const [bResp, dResp] = await Promise.all([
        supabase
          .from("lbs_bookings")
          .select(`
            id, reference_number, status, direction,
            scheduled_start, scheduled_end,
            external_ref, internal_ref,
            site:lbs_sites!lbs_bookings_site_id_fkey(id,name,code,timezone),
            bay:lbs_bays!lbs_bookings_bay_id_fkey(id,name,color)
          `)
          .or(companyId ? `created_by_user_id.eq.${user.id},approved_booker_id.eq.${companyId}` : `created_by_user_id.eq.${user.id}`)
          .gte("scheduled_start", new Date().toISOString())
          .order("scheduled_start", { ascending: true })
          .limit(50),
        companyId ? searchCompanyDrivers({ limit: 50 }) : Promise.resolve({ data: [] }),
      ]);
      if (bResp.error) toast.error(bResp.error.message);
      setBookings(bResp.data || []);
      setDrivers(dResp.data || []);
      setLoading(false);
    })();
  }, [user?.id, companyId]);

  return (
    <div className="space-y-6" data-testid="booker-dashboard">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Booker</h1>
        <p className="text-sm text-slate-500">
          Manage your bookings and drivers in one place.
        </p>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Link to="/bookings/new"><Card className="hover:border-slate-400 transition cursor-pointer">
          <CardContent className="flex items-center gap-3 p-4">
            <Plus className="h-5 w-5 text-blue-600" />
            <div><div className="font-medium">New booking</div><div className="text-xs text-slate-500">Pick a slot</div></div>
          </CardContent>
        </Card></Link>
        <Link to="/drivers"><Card className="hover:border-slate-400 transition cursor-pointer">
          <CardContent className="flex items-center gap-3 p-4">
            <Users className="h-5 w-5 text-blue-600" />
            <div><div className="font-medium">My drivers</div><div className="text-xs text-slate-500">Add / edit / remove</div></div>
          </CardContent>
        </Card></Link>
        <Link to="/bookings"><Card className="hover:border-slate-400 transition cursor-pointer">
          <CardContent className="flex items-center gap-3 p-4">
            <Calendar className="h-5 w-5 text-blue-600" />
            <div><div className="font-medium">All bookings</div><div className="text-xs text-slate-500">View, filter, export</div></div>
          </CardContent>
        </Card></Link>
      </div>

      {/* Upcoming bookings */}
      <Card>
        <CardHeader><CardTitle className="text-base">Upcoming bookings</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-slate-400">Loading…</div>
          ) : bookings.length === 0 ? (
            <div className="text-sm text-slate-400 italic">No upcoming bookings.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-slate-500 border-b">
                    <th className="py-2 pr-3">Ref</th>
                    <th className="py-2 pr-3">Start</th>
                    <th className="py-2 pr-3">Site / Bay</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Ext / Int ref</th>
                    <th className="py-2 pr-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="py-2 pr-3 font-mono text-xs">{b.reference_number}</td>
                      <td className="py-2 pr-3 whitespace-nowrap">{new Date(b.scheduled_start).toLocaleString("en-AU", { timeZone: b.site?.timezone || undefined, dateStyle: "medium", timeStyle: "short" })}</td>
                      <td className="py-2 pr-3">
                        {b.site?.name} {b.bay?.name ? `· ${b.bay.name}` : ""}
                      </td>
                      <td className="py-2 pr-3">
                        <span className={`text-xs px-2 py-0.5 rounded ${statusBadgeClass(b.status)}`}>
                          {b.status}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-xs text-slate-500">
                        {b.external_ref || "—"} / {b.internal_ref || "—"}
                      </td>
                      <td className="py-2 pr-3 text-right">
                        <Link to={`/bookings?id=${b.id}`} className="text-blue-600 hover:underline text-xs">Open</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* My drivers (overview only — full management on /drivers) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Truck className="h-4 w-4" /> My drivers
          </CardTitle>
        </CardHeader>
        <CardContent>
          {drivers.length === 0 ? (
            <div className="text-sm text-slate-400 italic">No drivers yet. <Link to="/drivers" className="text-blue-600 hover:underline">Add one</Link>.</div>
          ) : (
            <ul className="text-sm space-y-1">
              {drivers.slice(0, 10).map((d) => (
                <li key={d.id} className="flex justify-between border-b last:border-0 py-1">
                  <span>{[d.first_name, d.last_name].filter(Boolean).join(" ") || d.email}</span>
                  <span className="text-xs text-slate-500">{d.phone || d.email}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
